import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Layers,
  FileText,
  CheckSquare,
  PlayCircle,
  Check,
  X,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  PlanningState,
  PlanningStage,
  PlanningStageEntry,
  QualityScore,
} from '@/lib/api/epic-requests';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const stageIcons: Record<PlanningStage, React.ComponentType<{ className?: string }>> = {
  ANALYZE: Search,
  DECOMPOSE: Layers,
  DETAIL: FileText,
  EVALUATE: CheckSquare,
  VERIFY: PlayCircle,
};

const stageLabels: Record<PlanningStage, string> = {
  ANALYZE: 'Analyze',
  DECOMPOSE: 'Decompose',
  DETAIL: 'Detail',
  EVALUATE: 'Evaluate',
  VERIFY: 'Verify',
};

const STAGE_ORDER: PlanningStage[] = ['ANALYZE', 'DECOMPOSE', 'DETAIL', 'EVALUATE', 'VERIFY'];

// ---------------------------------------------------------------------------
// Hooks & helpers
// ---------------------------------------------------------------------------

function useElapsedTime(startedAt?: string, completedAt?: string) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) return;

    if (completedAt) {
      setElapsed(
        Math.floor(
          (new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 1000,
        ),
      );
      return;
    }

    const start = new Date(startedAt).getTime();
    setElapsed(Math.floor((Date.now() - start) / 1000));

    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startedAt, completedAt]);

  return elapsed;
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StageElapsedTime({ stage }: { stage: PlanningStageEntry }) {
  const elapsed = useElapsedTime(stage.startedAt, stage.completedAt);

  if (!stage.startedAt) return null;

  return (
    <span className="text-[10px] text-muted-foreground">{formatElapsed(elapsed)}</span>
  );
}

function QualityScoreBadge({ score }: { score: QualityScore }) {
  return (
    <span
      className={cn(
        'text-xs px-1.5 py-0.5 rounded',
        score.overall >= 80
          ? 'bg-green-900/30 text-green-300'
          : score.overall >= 60
            ? 'bg-yellow-900/30 text-yellow-300'
            : 'bg-red-900/30 text-red-300',
      )}
    >
      {score.overall}/100
    </span>
  );
}

function ViewEpicLink({ planningState }: { planningState: PlanningState }) {
  if (!planningState.epicId) return null;

  return (
    <Link
      to={`/epics/${planningState.epicIdentifier || planningState.epicId}`}
      className="inline-flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300"
    >
      <ExternalLink className="h-3 w-3" />
      View Epic
    </Link>
  );
}

function ConnectingLine({
  leftStatus,
  rightStatus,
}: {
  leftStatus: PlanningStageEntry['status'];
  rightStatus: PlanningStageEntry['status'];
}) {
  if (leftStatus === 'completed' && rightStatus === 'completed') {
    return <div className="h-0.5 flex-1 bg-green-400/60" />;
  }
  if (leftStatus === 'completed' && rightStatus === 'active') {
    return (
      <div className="h-0.5 flex-1 bg-gradient-to-r from-green-400/60 to-purple-400/60" />
    );
  }
  return <div className="h-0.5 flex-1 border-t-2 border-dashed border-muted-foreground/20" />;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface PlanningProgressPanelProps {
  planningState: PlanningState;
  className?: string;
}

export function PlanningProgressPanel({
  planningState,
  className,
}: PlanningProgressPanelProps) {
  const stageMap = new Map(planningState.stages.map((s) => [s.name, s]));
  const completedCount = planningState.stages.filter(
    (s) => s.status === 'completed',
  ).length;
  const progressPct = (completedCount / STAGE_ORDER.length) * 100;

  const errorStage = planningState.error?.stage;

  return (
    <div className={cn('w-full space-y-3', className)}>
      {/* Progress bar */}
      <div className="h-1 w-full rounded-full bg-muted/40 overflow-hidden">
        <div
          className="h-full rounded-full bg-purple-500 transition-all duration-500"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Stepper */}
      <div className="flex items-start">
        {STAGE_ORDER.map((stageName, idx) => {
          const entry = stageMap.get(stageName) ?? {
            name: stageName,
            status: 'pending' as const,
          };
          const Icon = stageIcons[stageName];
          const isLast = idx === STAGE_ORDER.length - 1;

          return (
            <div key={stageName} className="flex items-start flex-1 min-w-0">
              {/* Stage cell */}
              <div className="flex flex-col items-center gap-1 min-w-[56px]">
                {/* Circle */}
                <div
                  className={cn(
                    'relative flex h-10 w-10 items-center justify-center rounded-full transition-colors',
                    entry.status === 'pending' &&
                      'border-2 border-muted-foreground/30 bg-transparent',
                    entry.status === 'active' &&
                      'border-2 border-purple-400 bg-purple-500/20 animate-planning-pulse',
                    entry.status === 'completed' &&
                      'border-2 border-green-400 bg-green-500/20',
                    entry.status === 'failed' &&
                      'border-2 border-red-400 bg-red-500/20',
                    entry.status === 'skipped' &&
                      'border-2 border-muted-foreground/20 bg-muted/20',
                  )}
                >
                  {entry.status === 'completed' ? (
                    <Check className="h-4 w-4 text-green-400" />
                  ) : entry.status === 'failed' ? (
                    <X className="h-4 w-4 text-red-400" />
                  ) : (
                    <Icon
                      className={cn(
                        'h-4 w-4',
                        entry.status === 'active'
                          ? 'text-purple-400'
                          : 'text-muted-foreground/50',
                      )}
                    />
                  )}
                </div>

                {/* Label */}
                <span
                  className={cn(
                    'text-xs font-medium',
                    entry.status === 'active' && 'text-purple-400',
                    entry.status === 'completed' && 'text-green-400',
                    entry.status === 'failed' && 'text-red-400',
                    (entry.status === 'pending' || entry.status === 'skipped') &&
                      'text-muted-foreground/50',
                  )}
                >
                  {stageLabels[stageName]}
                </span>

                {/* Elapsed time */}
                <StageElapsedTime stage={entry} />

                {/* View Epic link (below DECOMPOSE) */}
                {stageName === 'DECOMPOSE' && <ViewEpicLink planningState={planningState} />}

                {/* Quality score (below EVALUATE) */}
                {stageName === 'EVALUATE' && planningState.qualityScore && (
                  <QualityScoreBadge score={planningState.qualityScore} />
                )}

                {/* Error message */}
                {errorStage === stageName && planningState.error && (
                  <div className="mt-1 flex items-start gap-1 max-w-[120px]">
                    <AlertCircle className="h-3 w-3 shrink-0 text-red-400 mt-0.5" />
                    <span className="text-[10px] text-red-400 leading-tight break-words">
                      {planningState.error.message}
                    </span>
                  </div>
                )}
              </div>

              {/* Connecting line */}
              {!isLast && (
                <div className="flex items-center pt-5 flex-1 mx-1">
                  <ConnectingLine
                    leftStatus={entry.status}
                    rightStatus={
                      stageMap.get(STAGE_ORDER[idx + 1] as PlanningStage)?.status ?? 'pending'
                    }
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pulse animation uses global animate-planning-pulse utility */}
    </div>
  );
}
